/**
 * SalesAccounts — Slice A (Phase 1 / 1.1) Accounts list.
 *
 * Read path: GET /v1/sales/accounts (A1) — name, #contacts, #open deals, open
 * value, last activity, ALL server-aggregated (listAccountsSQL is the source
 * of every number here; nothing is summed client-side). Search filters the
 * fetched list locally (≤ the server's 200-row page — the honest "Showing X
 * of Y" line says so).
 *
 * Money: open value is the server's FENCED GBP-comparable sum (currency-fence
 * increment 0: GBP rows as themselves, captured non-GBP rows by their base)
 * rendered as GBP; when the server excluded uncaptured non-GBP deals the row
 * SAYS so instead of silently under-counting.
 *
 * Visual (docs/design/stier/ADOPTION.md): the Contacts page treatment
 * verbatim — SectionTitle, .k-card + .sales-table (§8.4: one
 * .ops-table/.sales-table definition), the honest "Showing X of Y" line (§8.4:
 * no dead pagination controls), the k-btn family, the search input on the
 * control radius (§8.4 REFUSED list). The New Account drawer wears .k-drawer
 * (§8.4 portals). No new tokens.
 */

import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Plus, Search, X, Building2, Globe, Phone, FileText } from 'lucide-react'
import { SectionTitle, Avatar, LoadingState, ErrorState } from '../../DashboardShared'
import type { SalesAccountRow, SalesAccountView } from '../../types'
import { useAsync, fetchAccounts, createAccount } from '../../api'
import { money } from '../../../../lib/money'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { overlay } from '../../../../theme/tokens'
import { useSavedViews } from './useSavedViews'
import { ACCOUNTS_SCHEMA } from './savedViews'
import { SavedViewsBar } from './SavedViewsBar'

/** Body cell per ACCOUNTS_SCHEMA column id (columns-only list, D2). The Account
 *  cell keeps the avatar + website/phone subline; Website is also available as
 *  its own column through the Columns menu. */
function accountCell(a: SalesAccountRow): Record<string, React.ReactNode> {
  return {
    account: (
      <div className="flex items-center gap-3">
        <Avatar name={a.name} size={28} />
        <div className="min-w-0">
          <div className="text-13 font-semibold text-ink-primary truncate">{a.name}</div>
          <div className="text-11 text-ink-faint truncate">{a.website || a.phone || '—'}</div>
        </div>
      </div>
    ),
    website: <span className="text-12 text-ink-secondary">{a.website || '—'}</span>,
    contacts: <span className={`text-12 font-semibold ${a.contactCount > 0 ? 'text-ink-body' : 'text-ink-disabled'}`}>{a.contactCount}</span>,
    openDeals: <span className={`text-12 font-semibold ${a.openDealCount > 0 ? 'text-accent' : 'text-ink-disabled'}`}>{a.openDealCount}</span>,
    openValue: (
      <>
        <span className="text-13 text-ink-body">
          {a.openDealCount > 0 ? money(a.openValuePence / 100, 'GBP') : <span className="text-ink-disabled">—</span>}
        </span>
        {a.openValueExcluded > 0 && (
          <div className="text-11 text-ink-faint">
            {a.openValueExcluded} unconverted deal{a.openValueExcluded === 1 ? '' : 's'} excluded
          </div>
        )}
      </>
    ),
    lastActivity: <span className="text-12 text-ink-faint">{a.lastActivity}</span>,
  }
}

function Field({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-12 font-semibold text-ink-body mb-2">
        <span className="text-ink-faint">{icon}</span>{label}
      </label>
      {children}
    </div>
  )
}

/** A just-created account as a list row: the aggregates are honestly zero —
 *  nothing links to it yet — until the next fetch says otherwise. */
function rowFromView(a: SalesAccountView): SalesAccountRow {
  return { ...a, contactCount: 0, openDealCount: 0, openValuePence: 0, openValueExcluded: 0, lastActivity: '—' }
}

// ── New Account drawer (the New Contact drawer's shape; §8.4 .k-drawer) ──────

export function NewAccountDrawer({ onClose, onCreated }: {
  onClose: () => void
  onCreated: (account: SalesAccountView) => void
}) {
  const [name, setName]       = useState('')
  const [website, setWebsite] = useState('')
  const [phone, setPhone]     = useState('')
  const [notes, setNotes]     = useState('')
  const [saving, setSaving]   = useState(false)

  async function handleSave() {
    if (!name.trim()) { toast.error('Account name is required'); return }
    setSaving(true)
    try {
      const created = await createAccount({
        name: name.trim(),
        ...(website.trim() ? { website: website.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      })
      toast.success(`Account "${created.name}" created`)
      onCreated(created)
      onClose()
    } catch (e) {
      // 409 = the per-tenant name unique (0116); the server's message names it.
      toast.error(apiErrorMessage(e, 'Failed to create account'))
    } finally {
      setSaving(false)
    }
  }

  const inputCls = "w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint transition-all"

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer"
        style={{ width: 'min(580px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">New Account</div>
            <div className="text-12 text-ink-muted mt-1">A company your contacts and deals link to</div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          <Field label="Account name *" icon={<Building2 size={12} />}>
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="Orbital Systems" autoFocus className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Website" icon={<Globe size={12} />}>
              <input type="text" value={website} onChange={e => setWebsite(e.target.value)}
                placeholder="https://orbital.example" className={inputCls} />
            </Field>
            <Field label="Phone" icon={<Phone size={12} />}>
              <input type="text" value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="+44 20 …" className={inputCls} />
            </Field>
          </div>
          <Field label="Notes" icon={<FileText size={12} />}>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4}
              placeholder="Anything the team should know about this account" className={`${inputCls} resize-none`} />
          </Field>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          <button onClick={onClose} className="k-btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="k-btn k-btn--lg disabled:opacity-50">
            {saving ? 'Creating…' : 'Create Account'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── List page ─────────────────────────────────────────────────────────────

export function SalesAccounts({ onSelectAccount }: { onSelectAccount: (id: string) => void }) {
  const { data, loading, error } = useAsync(() => fetchAccounts(), [])
  // Optimistic adds sit in front of the fetched list — derived, never
  // mirrored into state from the fetch (no set-state-in-effect).
  const [added, setAdded]     = useState<SalesAccountRow[]>([])
  const [search, setSearch]   = useState('')
  const [showNew, setShowNew] = useState(false)
  // Columns-only saved views (D2): no filters, no sort — search stays local.
  const sv = useSavedViews(ACCOUNTS_SCHEMA)

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const accounts = [...added, ...(data ?? [])]

  const q = search.trim().toLowerCase()
  const filtered = q
    ? accounts.filter(a => a.name.toLowerCase().includes(q) || a.website.toLowerCase().includes(q))
    : accounts
  const withOpen = accounts.filter(a => a.openDealCount > 0).length
  const visibleCols = new Set(sv.state.columns)
  const shownColumns = ACCOUNTS_SCHEMA.columns.filter(c => visibleCols.has(c.id))

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Accounts"
        subtitle={`${accounts.length} accounts · ${withOpen} with open deals`}
        action={
          <button className="k-btn k-btn--lg inline-flex items-center gap-2" onClick={() => setShowNew(true)}>
            <Plus size={14} /> New Account
          </button>
        }
      />

      {/* Saved views + column picker (Slice D / D2 — columns only) */}
      <SavedViewsBar sv={sv} schema={ACCOUNTS_SCHEMA} baseLabel="All accounts" />

      {/* Search (the Contacts search box, same classes) */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="ml-auto relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search accounts…"
            className="h-8 pl-7 pr-3 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent w-56" />
        </div>
      </div>

      <div className="k-card">
        <table className="closr-table sales-table w-full">
          <thead>
            <tr>
              {shownColumns.map(col => <th key={col.id} className="text-left">{col.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.map(a => {
              const cells = accountCell(a)
              return (
                <tr key={a.id}
                  className="cursor-pointer hover:bg-surface-row-hover transition-colors"
                  onClick={() => onSelectAccount(a.id)}>
                  {shownColumns.map(col => <td key={col.id}>{cells[col.id]}</td>)}
                </tr>
              )
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={shownColumns.length} className="text-13 text-ink-muted text-center py-8">
                  {accounts.length === 0
                    ? 'No accounts yet — create one, or add a deal with a company.'
                    : 'No accounts match your search.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* The honest count line (§8.4): no pagination exists, none is drawn. */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-edge-row">
          <span className="text-11 text-ink-faint">Showing {filtered.length} of {accounts.length} accounts</span>
        </div>
      </div>

      {showNew && (
        <NewAccountDrawer onClose={() => setShowNew(false)}
          onCreated={a => setAdded(prev => [rowFromView(a), ...prev])} />
      )}
    </div>
  )
}
